// Cikk-oldal (spec 6.1). Ez a Hub legfontosabb képernyője: itt történik a tényleges munka, a
// szöveg elolvasása. Ezért minden más elem — kenyérmorzsa, meta, címkék — halkabb, mint a törzs,
// és a mérték (68ch) a törzsé, nem a képernyőé.
import { ClockIcon, GlobeAltIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, Navigate, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CommentSection } from '@/features/comments/components/comment-section';
import { useKnowledgeArticle, useKnowledgeSpace } from '@/features/knowledge/api';
import { ArticleFooterNav } from '@/features/knowledge/components/article-footer-nav';
import { DraftBadge } from '@/features/knowledge/components/draft-badge';
import { KnowledgeLayout } from '@/features/knowledge/components/knowledge-layout';
import { Markdown } from '@/features/knowledge/components/markdown';
import { StateIcons } from '@/features/knowledge/components/state-icons';
import { TocNav } from '@/features/knowledge/components/toc-nav';
import { extractHeadings } from '@/features/knowledge/lib/toc';
import { requireAuth } from '@/lib/auth';
import { useHasPermission } from '@/lib/use-permission';

export const Route = createFileRoute('/hub/$spaceSlug/$categorySlug/$articleSlug')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: ArticlePage,
});

const dateFormat = new Intl.DateTimeFormat('hu-HU', { dateStyle: 'long' });

/** Olvasási idő ~200 szó/perc alapján, legalább 1 perc. A hosszú cikkeknél ez érdemi információ. */
function readingMinutes(body: string): number {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function ArticlePage(): JSX.Element {
  const { t } = useTranslation();
  const params = Route.useParams();
  const query = useKnowledgeArticle(params);
  // Ugyanaz a cache-kulcs, amit az oldalsáv is használ → a lapozó adata plusz kérés nélkül megvan.
  const spaceQuery = useKnowledgeSpace(params.spaceSlug);
  const canEdit = useHasPermission(PERMISSIONS.articleManage);

  const page = query.data?.status === 200 ? query.data.body : null;
  const spacePage = spaceQuery.data?.status === 200 ? spaceQuery.data.body : null;
  const headings = useMemo(() => (page ? extractHeadings(page.article.body) : []), [page]);

  // Átnevezett/áthelyezett cikk: a kért útvonal nem a kanonikus → csendes átirányítás (replace).
  if (
    page &&
    (page.canonical.spaceSlug !== params.spaceSlug ||
      page.canonical.categorySlug !== params.categorySlug ||
      page.canonical.articleSlug !== params.articleSlug)
  ) {
    return (
      <Navigate
        to="/hub/$spaceSlug/$categorySlug/$articleSlug"
        params={{
          spaceSlug: page.canonical.spaceSlug,
          categorySlug: page.canonical.categorySlug,
          articleSlug: page.canonical.articleSlug,
        }}
        replace
      />
    );
  }

  if (query.isLoading) {
    return (
      <KnowledgeLayout activeSpaceSlug={params.spaceSlug}>
        <Skeleton className="h-4 w-64" />
        <Skeleton className="mt-6 h-10 w-3/4" />
        <Skeleton className="mt-8 h-64 w-full max-w-2xl" />
      </KnowledgeLayout>
    );
  }

  if (!page) {
    return (
      <KnowledgeLayout activeSpaceSlug={params.spaceSlug}>
        <div className="py-16">
          <p className="text-read-lg font-medium">{t('knowledge.notFound')}</p>
          <Button asChild variant="secondary" className="mt-5">
            <Link to="/tudastar">{t('knowledge.backHome')}</Link>
          </Button>
        </div>
      </KnowledgeLayout>
    );
  }

  return (
    <KnowledgeLayout activeSpaceSlug={page.space.slug} toc={<TocNav items={headings} />}>
      <KnowledgeCrumbs
        path={{ space: page.space, category: page.category }}
        article={{ title: page.article.title, slug: null }}
      />

      <article className="mt-7">
        <header className="max-w-[68ch]">
          <div className="flex items-start justify-between gap-6">
            {/* A VÁZLAT jelölése a cím mellett: aki ide megérkezik (pl. a szekció-oldal listájából),
                azonnal tudja, hogy befejezetlen tartalmat olvas — és hogy ez a publikus súgóban nem
                látszik. A publikus úton vázlat nem is nyílik meg, tehát ott ez sosem jelenik meg. */}
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <h1 className="text-read-2xl font-semibold tracking-tight">{page.article.title}</h1>
              {page.article.status === 'draft' && <DraftBadge />}
              <StateIcons
                published={page.article.status === 'published'}
                publiclyVisible={page.publiclyVisible}
              />
            </div>
            {/* A műveletek EGYÜTT, a cím mellett — hogy ne kelljen keresgélni, mit lehet itt tenni. */}
            {canEdit && (
              <div className="mt-1 flex shrink-0 items-center gap-1">
                <Button asChild variant="secondary" size="sm" className="gap-1.5">
                  <Link to="/szerkesztes/$articleId" params={{ articleId: page.article.id }}>
                    <PencilSquareIcon className="h-4 w-4" aria-hidden />
                    {t('editor.edit')}
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5">
                  <Link to="/valtozatok/$articleId" params={{ articleId: page.article.id }}>
                    <ClockIcon className="h-4 w-4" aria-hidden />
                    {t('revisions.open')}
                  </Link>
                </Button>
              </div>
            )}
          </div>

          {page.article.summary && (
            <p className="text-muted-foreground text-read-base mt-3 font-serif">
              {page.article.summary}
            </p>
          )}

          {/* Meta-sor: mikor frissült, mennyi idő elolvasni. Halk, de a döntéshez kell. */}
          <p className="text-subtle border-border mt-5 flex flex-wrap items-center gap-x-1.5 border-b pb-5 text-sm">
            <span>
              {t('knowledge.updated', {
                date: dateFormat.format(new Date(page.article.updatedAt)),
              })}
            </span>
            <span aria-hidden>·</span>
            <span>{t('knowledge.readingTime', { minutes: readingMinutes(page.article.body) })}</span>
            {/* Átjáró a publikus súgóba — CSAK ha a cikk tényleg kijut oda (a szerver az egész
                hierarchiát nézi, lásd `publiclyVisible`). Nem szerkesztői privilégium: a
                támogatásban a leggyakoribb művelet épp az, hogy a publikus linket kell odaadni
                egy ügyfélnek. */}
            {page.publiclyVisible && (
              <>
                <span aria-hidden>·</span>
                <Link
                  to="/sugo/$spaceSlug/$categorySlug/$articleSlug"
                  params={{
                    spaceSlug: page.canonical.spaceSlug,
                    categorySlug: page.canonical.categorySlug,
                    articleSlug: page.canonical.articleSlug,
                  }}
                  className="hover:text-foreground inline-flex items-center gap-1 underline decoration-dotted underline-offset-4 transition-colors duration-150"
                >
                  <GlobeAltIcon className="h-3.5 w-3.5" aria-hidden />
                  {t('knowledge.publicView')}
                </Link>
              </>
            )}
          </p>
        </header>

        <div className="mt-8">
          <Markdown source={page.article.body} headings={headings} />
        </div>

        {page.article.tags.length > 0 && (
          <footer className="border-border mt-14 max-w-[68ch] border-t pt-5">
            <ul className="flex flex-wrap gap-2" aria-label={t('knowledge.tags')}>
              {page.article.tags.map((tag) => (
                <li key={tag}>
                  <Link
                    to="/tag/$tag"
                    params={{ tag }}
                    className="border-border text-muted-foreground hover:border-primary/50 hover:text-primary rounded-full border px-3 py-1 text-sm transition-colors duration-150"
                  >
                    {tag}
                  </Link>
                </li>
              ))}
            </ul>
          </footer>
        )}

        {/* Belső beszélgetés a cikkről. A publikus súgó cikk-oldalán ez NEM jelenik meg. */}
        <CommentSection articleId={page.article.id} />

        <ArticleFooterNav
          space={spacePage}
          categorySlug={page.canonical.categorySlug}
          articleSlug={page.article.slug}
        />
      </article>
    </KnowledgeLayout>
  );
}
